import React from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

/* ---------------- helpers ---------------- */
function fmtDateTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR');
}
function fmtTimeTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
function safeText(v, fallback = '—') {
  const s = (v ?? '').toString().trim();
  return s ? s : fallback;
}
function short(v, n = 18) {
  const s = safeText(v, '');
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
function fmtTry(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('tr-TR')} ₺`;
}
function rsvpChip(status, reportedAt) {
  const s = String(status ?? '').toUpperCase();
  const hasTime = Boolean(reportedAt);

  // null / UNKNOWN / boş => işaretlemedi
  if (!s || s === 'UNKNOWN' || s === 'NULL') {
    return { label: 'İşaretlemedi', color: 'default', tip: hasTime ? fmtDateTR(reportedAt) : '' };
  }

  if (s === 'YES')
    return { label: 'Geleceğim', color: 'success', tip: hasTime ? fmtDateTR(reportedAt) : '' };
  if (s === 'NO')
    return { label: 'Gelmeyeceğim', color: 'error', tip: hasTime ? fmtDateTR(reportedAt) : '' };

  // fallback
  return { label: s, color: 'default', tip: hasTime ? fmtDateTR(reportedAt) : '' };
}

function attendanceChip(status) {
  const v = String(status ?? '').toUpperCase();
  if (v === 'ATTENDED') return { label: 'Katıldı', color: 'success' };
  if (v === 'NO_SHOW') return { label: 'Gelmedi', color: 'error' };
  if (v === 'CANCELLED') return { label: 'İptal', color: 'warning' };
  if (v) return { label: v, color: 'default' };
  return { label: '—', color: 'default' };
}

function paymentLabel(v) {
  const s = String(v ?? '').toUpperCase();
  if (s === 'CASH') return 'Nakit';
  if (s === 'CARD') return 'Kart';
  if (s === 'TRANSFER') return 'Havale';
  if (s === 'PACKAGE') return 'Paket';
  return s || '—';
}

function usageLabel(r) {
  const used = Number(r?.packageUsedSessionIndex);
  const total = Number(r?.packageTotalSession);
  if (Number.isFinite(used) && Number.isFinite(total) && total > 0) return `${used} / ${total}`;
  // bazen sadece index gelebilir
  if (Number.isFinite(used)) return String(used);
  return null;
}

function usageColor(r) {
  const used = Number(r?.packageUsedSessionIndex);
  const total = Number(r?.packageTotalSession);
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return 'default';
  if (used >= total) return 'error';
  if (used >= total - 1) return 'warning';
  return 'info';
}

/* ---------------- component ---------------- */
/**
 * Beklenen POJO satır örneği:
 * {
 *   id: 1,
 *   startAt: '2026-02-10T10:00:00',
 *   memberName: 'Ayşe Yılmaz',
 *   sessionType: 'Reformer',
 *   attendanceStatus: 'ATTENDED',
 *   packageName: '8 Ders',
 *   packagePeriod: 'Şubat 2026',
 *   packagePriceTry: 2400,
 *   packageUsedSessionIndex: 3, // ✅ bu seans kaçıncı kullanım
 *   packageTotalSession: 8,     // ✅ paket toplam hak
 *   paymentType: 'PACKAGE',
 *   isGroup: true,
 *   note: 'Bel ağrısı dikkat'
 * }
 */
export function StaffSessionsTable({ rows = [], limit = 30 }) {
  const visibleRows = (rows || []).slice(0, limit);

  const thSx = {
    textAlign: 'left',
    px: 0.9,
    py: 0.75,
    fontSize: 10.5,
    fontWeight: 900,
    borderBottom: '1px solid',
    borderColor: 'grey.200',
    whiteSpace: 'nowrap',

    // ✅ sticky header (yatay kaydırmada da rahat)
    position: 'sticky',
    top: 0,
    zIndex: 3,
    bgcolor: 'grey.50',
    backdropFilter: 'blur(6px)',
  };

  const tdSx = {
    px: 0.75,
    py: 0.45,
    fontSize: 10.75,
    borderBottom: '1px solid',
    borderColor: 'grey.100',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  function groupBySession(rows = []) {
    const map = new Map();

    for (const r of rows) {
      const key = r.sessionId;
      if (!map.has(key)) {
        map.set(key, {
          sessionId: r.sessionId,
          startAt: r.startAt,
          dayISO: r.dayISO,
          startMin: r.startMin,
          durationMin: r.durationMin,
          trainerName: r.trainerName,
          activityName: r.activityName,
          sessionType: r.sessionType,
          billingMode: r.billingMode,
          // isGroup: backend’den gelen boolean katılımcı bazında aynı olur ama
          // en doğru hesap: participants count > 1
          participants: [],
        });
      }
      map.get(key).participants.push(r);
    }

    // isGroup’u en doğru şekilde hesapla
    const sessions = Array.from(map.values()).map((s) => ({
      ...s,
      isGroup: (s.participants?.length || 0) > 1,
    }));

    // sıralama (en yeni üstte)
    sessions.sort((a, b) => new Date(b.startAt) - new Date(a.startAt));
    return sessions;
  }
  return (
    <MDBox sx={{ overflowX: 'auto' }}>
      <MDBox
        component="table"
        sx={{
          width: '100%',
          minWidth: 1480,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        {/* HEADER */}
        <MDBox component="thead">
          <MDBox component="tr" sx={{ bgcolor: 'grey.50' }}>
            <MDBox component="th" sx={{ ...thSx, width: 86 }}>
              Tarih
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 58 }}>
              Saat
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 180 }}>
              Üye
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 130 }}>
              Seans Türü
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 92 }}>
              Katılım
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 120 }}>
              Üye Beyanı
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 190 }}>
              Paket (Dönem)
            </MDBox>
            {/* ✅ yeni */}
            <MDBox component="th" sx={{ ...thSx, width: 92 }}>
              Kullanım
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 104 }}>
              Paket Fiyat
            </MDBox>

            <MDBox component="th" sx={{ ...thSx, width: 104 }}>
              Ödenen
            </MDBox>

            <MDBox component="th" sx={{ ...thSx, width: 96 }}>
              Ödeme
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 78 }}>
              Grup?
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 220 }}>
              Not
            </MDBox>
            <MDBox component="th" sx={{ ...thSx, width: 140 }}>
              Yoklama Zamanı
            </MDBox>

            <MDBox component="th" sx={{ ...thSx, width: 140 }}>
              Alan Kişi
            </MDBox>
          </MDBox>
        </MDBox>

        {/* BODY */}
        <MDBox component="tbody">
          {visibleRows.map((r, i) => {
            const debt = Number(r?.planDebtTry) || 0;
            const isDebt = debt > 0;
            const att = attendanceChip(r.attendanceStatus);
            const rsvp = rsvpChip(r.rsvpStatus, r.rsvpReportedAt);
            const pkg = [safeText(r.packageName, ''), safeText(r.packagePeriod, '')]
              .filter(Boolean)
              .join(' • ');

            const usage = usageLabel(r);

            return (
              <MDBox
                key={r.participantId ?? r.sessionId ?? i}
                component="tr"
                sx={{
                  ...(isDebt && {
                    bgcolor: 'error.50',
                    borderLeft: '4px solid',
                    borderLeftColor: 'error.main',
                  }),
                  '&:hover': {
                    bgcolor: isDebt ? 'error.100' : 'grey.50',
                  },
                }}
              >
                <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
                  {fmtDateTR(r.startAt)}
                </MDBox>
                <MDBox component="td" sx={tdSx}>
                  {fmtTimeTR(r.startAt)}
                </MDBox>
                <MDBox component="td" sx={{ ...tdSx, fontWeight: 700 }}>
                  <Tooltip title={safeText(r.memberName)}>
                    <span>{short(r.memberName, 24)}</span>
                  </Tooltip>
                </MDBox>
                <MDBox component="td" sx={tdSx}>
                  <Tooltip title={safeText(r.sessionType)}>
                    <span>{short(r.sessionType, 18)}</span>
                  </Tooltip>
                </MDBox>
                <MDBox component="td" sx={tdSx}>
                  <Chip size="small" variant="outlined" label={att.label} color={att.color} />
                </MDBox>
                <MDBox component="td" sx={tdSx}>
                  <Tooltip
                    title={
                      r.rsvpReportedAt
                        ? `${fmtDateTR(r.rsvpReportedAt)} ${fmtTimeTR(r.rsvpReportedAt)}`
                        : rsvp.label
                    }
                    arrow
                  >
                    <span>
                      <Chip size="small" variant="outlined" label={rsvp.label} color={rsvp.color} />
                    </span>
                  </Tooltip>
                </MDBox>

                <MDBox component="td" sx={tdSx}>
                  <Tooltip title={pkg || '—'}>
                    <span>{pkg ? short(pkg, 34) : '—'}</span>
                  </Tooltip>
                </MDBox>
                {/* ✅ kullanım */}
                <MDBox component="td" sx={tdSx}>
                  {usage ? (
                    <Chip size="small" variant="outlined" label={usage} color={usageColor(r)} />
                  ) : (
                    '—'
                  )}
                </MDBox>
                <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
                  {fmtTry(r.planPriceTry)}
                </MDBox>

                <MDBox
                  component="td"
                  sx={{
                    ...tdSx,
                    fontWeight: 800,
                    color: (Number(r.planPaidTry) || 0) > 0 ? 'success.main' : 'text.primary',
                  }}
                >
                  {fmtTry(r.planPaidTry)}
                </MDBox>

                <MDBox component="td" sx={tdSx}>
                  {paymentLabel(r.paymentMethod)}
                </MDBox>
                <MDBox component="td" sx={tdSx}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={r.isGroup ? 'Grup' : 'Bireysel'}
                    color={r.isGroup ? 'info' : 'default'}
                  />
                </MDBox>

                <MDBox component="td" sx={tdSx}>
                  {r.note ? (
                    <Tooltip title={safeText(r.note)}>
                      <span>{short(r.note, 44)}</span>
                    </Tooltip>
                  ) : (
                    '—'
                  )}
                </MDBox>
                {/* Yoklama zamanı */}
                <MDBox component="td" sx={tdSx}>
                  {r.attendanceTakenAt ? (
                    <>
                      {fmtDateTR(r.attendanceTakenAt)}
                      <br />
                      <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                        {fmtTimeTR(r.attendanceTakenAt)}
                      </MDTypography>
                    </>
                  ) : (
                    '—'
                  )}
                </MDBox>

                {/* Alan kişi */}
                <MDBox component="td" sx={tdSx}>
                  {safeText(r.attendanceTakenByName)}
                </MDBox>
              </MDBox>
            );
          })}

          {visibleRows.length === 0 && (
            <MDBox component="tr">
              <MDBox component="td" colSpan={13} sx={{ py: 2 }}>
                <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                  Seans bulunamadı.
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <MDBox mt={0.75} display="flex" justifyContent="space-between">
        <MDTypography variant="caption" sx={{ opacity: 0.6 }}>
          Gösterilen: {visibleRows.length} / {rows.length}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}
